import { ChangeDetectorRef, Component, EventEmitter, OnChanges } from '@angular/core';
import { filter } from 'rxjs/operators';
import { HistoricalAddressContext, HistoricalCustomerAddressModalComponent } from '../customer/address/historical-customer-address-modal.component';
import { Country } from '../Foundation/models/enums/country.enum';
import { County } from '../foundation/models/enums/county.enum';
import { Jurisdiction } from '../foundation/models/enums/jurisdiction.enum';
import { BaseComponent } from '../shared/base-components/base.component';
import { ZipCodePipe } from '../shared/pipes/zip-code.pipe';
import { ModalSize } from '../shared/services/modal/mvd-modal.enum';
import { MvdModalService } from '../shared/services/modal/mvd-modal.service';
import { AddressModalComponent, AddressModalContext } from './address-modal.component';
import { Address } from './models/address.model';
import { AddressType } from './models/enums/address-type.enum';
import { AddressService } from './services/address.service';

@Component({
    selector: 'address-component',
    templateUrl: './address.html',
    inputs: [
        'entityId',
        'addresses',
        'componentTitle',
        'componentTypeName',
        'canEdit',
        'canSetUndeliverable',
        'showHistoryButton',
        'isACP',
        'canSelect',
        'selectedAddress'],
    outputs: ['selectedAddressChange', 'onAddressesUpdated']
})
export class AddressComponent extends BaseComponent implements OnChanges {
    // Inputs
    public addresses: any[];
    public entityId: number;
    public componentTitle: string;
    public componentTypeName: string;
    public canEdit = false;
    public canSetUndeliverable: boolean;
    public isACP = true;
    public curbSideDelivery = true;
    public showHistoryButton = false;
    public canSelect = false;
    public selectedAddress = 0;
    // Outputs
    public selectedAddressChange: EventEmitter<number> = new EventEmitter<number>();
    public onAddressesUpdated: EventEmitter<any> = new EventEmitter<any>();

    // General Properties
    public selection: Address;
    private addressTypes = new AddressType();
    public countries = new Country();
    private counties = new County();
    private states = new Jurisdiction();
    private zipCodePipe = new ZipCodePipe();

    // Properties to control the add address link
    public addLinkText = '';
    public showAddLink = false;

    constructor(
        public modalService: MvdModalService,
        public addressService: AddressService,
        public _changeDetectionRef: ChangeDetectorRef) {
        super();
    }

    public ngOnInit() {
        if (this.entityId != null) {
            // this.populateAddress(this.customerNumber);
        } else if (this.addresses !== undefined) {
            this.denormalizeData();
        }
    }

    /**
     * Force angular to check for changes.
     */
    private detectFormChanges = (): void => {
        this._changeDetectionRef.detectChanges();
    };

    public ngOnChanges() {
        if (this.addresses !== undefined) {
            this.denormalizeData();

            if (this.selectedAddress != (this.selection ? this.selection.id : 0)) {
                this.selection = this.addresses.find((v: Address) => v.id == this.selectedAddress);
            }
        }
    }

    public denormalizeData = (): void => {
        this.filterUndeliverableAddresses();
        for (const address of this.addresses) {
            if (this.componentTypeName.indexOf('individual-customer') > -1 || this.componentTypeName.indexOf('organization-customer') > -1) {
                if (address.addressTypeId == this.addressTypes.Physical.id) {
                    if (this.componentTypeName.indexOf('individual-customer') > -1) {
                        address.addressType = 'Residential';
                    } else if (this.componentTypeName.indexOf('organization-customer') > -1) {
                        address.addressType = 'Commercial';
                    }
                } else {
                    address.addressType = this.getValues(address.addressTypeId, this.addressTypes);
                }
            }

            address.country = this.getValues(address.countryId, this.countries);
            address.county = this.getValues(address.countyId, this.counties);

            if ((address.countryId == this.countries.US.id) || (address.countryId == this.countries.MX.id) || (address.countryId == this.countries.CA.id) || (address.countryId == this.countries.DE.id) || (address.countryId == this.countries.KR.id) || (address.countryId == this.countries.TW.id)) {
                address.state = this.getValues(address.jurisdictionId, this.states);
            } else {
                address.state = address.internationalJurisdiction;
            }

            // Map the customerId,vehicleId,biz transactionId to address depending on what was sent in
            if (address.customerNumberId != null) {
                address.entityId = address.customerNumberId;
            } else if (address.vehicleId != null) {
                address.entityId = address.vehicleId;
            } else if (address.businessTransactionItemKeyId != null) {
                address.entityId = address.businessTransactionItemKeyId;
            }

            if (address.addressTypeId == 7 || address.addressTypeId == 6) {
                address.isReadOnly = true;
            }
        }
        this.updateAddLink();
    };

    public populateAddresses = (): void => {
        if (this.componentTypeName.indexOf('customer') > -1 && this.entityId != null) {
            this.addresses = [];

            this.addressService.getCustomerAddresses(this.entityId).subscribe(result => {
                this.addresses = result;

                this.denormalizeData();
                this.onAddressesUpdated.emit(result);
            });
        }
    };

    public updateAddLink = (): void => {
        if (this.componentTypeName.indexOf('individual-customer') > -1 || this.componentTypeName.indexOf('organization-customer') > -1) {
            let hasPhysical = false;
            let hasMailing = false;
            let zipToForCurbSideDelivery = '';
            let isCurbSide = 0;
            for (const address of this.addresses) {
                if (!address.isUndeliverable || address.addressTypeId != this.addressTypes.Mailing.id) {
                    if (address.addressTypeId == this.addressTypes.Physical.id) {
                        hasPhysical = true;
                        zipToForCurbSideDelivery = address.postalZipCode;
                    }

                    if (address.addressTypeId == this.addressTypes.Mailing.id) {
                        hasMailing = true;
                    }

                    if (address.isCurbSide) {
                        isCurbSide = isCurbSide + 1;
                    }
                }
            }

            if (hasMailing && !hasPhysical) {
                this.showAddLink = true;
                if (this.componentTypeName.indexOf('individual-customer') > -1) {
                    this.addLinkText = ' Add Residential Address';
                } else if (this.componentTypeName.indexOf('organization-customer') > -1) {
                    this.addLinkText = ' Add Commercial Address';
                }

                if ((null == this.componentTypeName) || (this.componentTypeName.indexOf('-physical') == -1)) {
                    this.componentTypeName += '-physical';
                }
            } else if (!hasMailing && hasPhysical) {
                this.showAddLink = true;
                this.addLinkText = ' Add Mailing Address';

                if ((null == this.componentTypeName) || (this.componentTypeName.indexOf('-mailing') == -1)) {
                    this.componentTypeName += '-mailing';
                }
            } else if (hasMailing && hasPhysical) {
                this.showAddLink = false;
                this.addLinkText = '';
            } else {
                this.showAddLink = true;
                this.addLinkText = ' Add Address';
            }

            // Check curbside delivery
            if (hasMailing) {
                this.curbSideDelivery = true;
            } else if (isCurbSide != 0) {
                this.curbSideDelivery = false;
            } else if (hasPhysical == true && hasMailing == false && !this.addresses[0].curbData) {
                this.addressService.hasCurbsideDelivery(zipToForCurbSideDelivery).subscribe(result => {
                    this.curbSideDelivery = result.result;
                });
            }
        }
    };

    public getValues = (id: number, types: any): string => {
        try {
            if (id != null) {
                return types.FromId(id).displayValue;
            } else {
                return '';
            }
        } catch (ex) {
            return '';
        }
    };

    private setAddress(addr: Address): Address {
        let newAddress: Address = new Address();

        newAddress.addressNumberId = addr.addressNumberId;
        newAddress.id = addr.id;
        newAddress.addressTypeId = addr.addressTypeId;
        newAddress.jurisdictionId = addr.jurisdictionId;
        newAddress.countyId = addr.countyId;
        newAddress.countryId = addr.countryId;
        newAddress.isMilitary = addr.isMilitary;
        newAddress.inCareOf = addr.inCareOf;
        newAddress.addressLine1 = addr.addressLine1;
        newAddress.addressLine2 = addr.addressLine2;
        newAddress.latitude = addr.latitude;
        newAddress.longitude = addr.longitude;
        newAddress.city = addr.city;
        newAddress.postalZipCode = addr.postalZipCode;
        newAddress.postalDeliveryCode = addr.postalDeliveryCode;
        newAddress.isOveridden = addr.isOveridden;
        newAddress.inCareOf = addr.inCareOf;
        newAddress.isUndeliverable = addr.isUndeliverable;
        newAddress.undeliverableDate = addr.undeliverableDate;
        newAddress.isPreferredContactMethod = addr.isPreferredContactMethod;
        newAddress.nonMilitaryJurisdictionId = addr.nonMilitaryJurisdictionId;
        newAddress.militaryJurisdictionId = addr.militaryJurisdictionId;
        newAddress.militaryCityPrefixTypeId = addr.militaryCityPrefixTypeId;
        newAddress.emissionAreaTypeId = addr.emissionAreaTypeId;
        newAddress.addressConfidenceRatingTypeId = addr.addressConfidenceRatingTypeId;
        newAddress.geocodeConfidenceRatingTypeId = addr.geocodeConfidenceRatingTypeId;
        newAddress.landCategoryTypeId = addr.landCategoryTypeId;
        newAddress.updatedBy = addr.updatedBy;
        newAddress.updatedDate = addr.updatedDate;
        newAddress.viewId = addr.viewId;
        newAddress.addressType = addr.addressType;
        newAddress.county = addr.county;
        newAddress.country = addr.country;
        newAddress.state = addr.state;
        newAddress.customerNumberId = addr.customerNumberId;
        newAddress.vehicleId = addr.vehicleId;
        newAddress.businessTransactionItemKeyId = addr.businessTransactionItemKeyId;

        if (addr.customerNumberId != null) {
            newAddress.entityId = addr.customerNumberId;
        } else if (addr.vehicleId != null) {
            newAddress.entityId = addr.vehicleId;
        } else if (addr.businessTransactionItemKeyId != null) {
            newAddress.entityId = addr.businessTransactionItemKeyId;
        }

        return newAddress;
    }

    public updateAddress = (addressNumberId: number, readOnly: boolean = false): void => {
        let updateComponentTypeName = this.componentTypeName;
        if (this.componentTypeName.indexOf('individual-customer') > -1) {
            updateComponentTypeName = 'individual-customer';
        } else if (this.componentTypeName.indexOf('organization-customer') > -1) {
            updateComponentTypeName = 'organization-customer';
        }

        let hasPhysical = false;
        let hasMailing = false;

        for (const address of this.addresses) {
            if (address.addressTypeId == this.addressTypes.Physical.id) {
                hasPhysical = true;
            }

            if (!address.isUndeliverable) {
                if (address.addressTypeId == this.addressTypes.Mailing.id) {
                    hasMailing = true;
                }
            }
        }

        if (hasMailing && !hasPhysical) {
            if (updateComponentTypeName.indexOf('-physical') == -1) {
                updateComponentTypeName += '-physical';
            }
        } else if (!hasMailing && hasPhysical) {
            if (updateComponentTypeName.indexOf('-mailing') == -1) {
                updateComponentTypeName += '-mailing';
            }
        }

        var data: AddressModalContext;

        if (this.isACP && readOnly) {
            data = new AddressModalContext(addressNumberId, this.entityId, updateComponentTypeName, false, this.canSetUndeliverable, true);
        } else {
            data = new AddressModalContext(addressNumberId, this.entityId, updateComponentTypeName, false, this.canSetUndeliverable);
        }

        const tempAddresses = this.addresses;
        let existingAddress = false;
        this.modalService.open(AddressModalComponent, data, ModalSize.Medium).afterClosed()
            .pipe(filter(result => result && !this.isACP))
            .subscribe(a => {
                if (this.componentTypeName.indexOf('customer') > -1 && this.entityId != null) {
                    this.populateAddresses();
                } else {
                    // Update address list with saved address
                    tempAddresses.forEach((item) => {
                        if (item.id == a.id) {
                            existingAddress = true;
                            item = this.setAddress(a);
                        }
                    });

                    if (!existingAddress) {
                        tempAddresses.push(this.setAddress(a));
                    }

                    this.addresses = tempAddresses;

                    this.populateAddresses();
                    this.detectFormChanges();
                    this.denormalizeData();
                }
            });
    };

    public showHistory = (): void => {
        const data = new HistoricalAddressContext(this.entityId);
        this.modalService.open(HistoricalCustomerAddressModalComponent, data);
    };

    public rowSelected = (event: any): void => {
        const selectedAddressId: number = this.selection ? this.selection.id : 0;
        this.selectedAddressChange.emit(selectedAddressId);
    };

    private filterUndeliverableAddresses = (): void => {
        this.addresses = this.addresses.filter((address) => !address.isUndeliverable || address.addressTypeId != this.addressTypes.Mailing.id);
    };
}
